/**
 * Utility to handle CDN URLs for assets
 */

/**
 * Convert a logo URL to use the CDN if available
 * @param url - The original logo URL
 * @returns The CDN URL if applicable, otherwise the original URL
 */
export function getCdnUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  
  // If URL is already a full URL (http/https), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If URL is a data URL, return as is
  if (url.startsWith('data:')) {
    return url;
  }
  
  // Get CDN base URL from environment variable
  const cdnBaseUrl = process.env.NEXT_PUBLIC_VECTORIZE_CDN_BASE_URL;
  
  // If no CDN URL is configured, return the original URL
  if (!cdnBaseUrl) {
    return url;
  }
  
  // If URL starts with /, it's a relative path - prepend CDN URL
  if (url.startsWith('/')) {
    return `${cdnBaseUrl}${url}`;
  }
  
  // Otherwise, assume it's a path that needs CDN URL and /
  return `${cdnBaseUrl}/${url}`;
}

/**
 * Get the appropriate logo URL based on theme
 * @param lightLogo - The light theme logo URL
 * @param darkLogo - The dark theme logo URL
 * @param isDark - Whether dark theme is active
 * @returns The appropriate CDN URL for the logo
 */
export function getThemedLogoUrl(
  lightLogo: string | undefined | null,
  darkLogo: string | undefined | null,
  isDark: boolean
): string | undefined {
  const logoUrl = isDark && darkLogo ? darkLogo : lightLogo;
  return getCdnUrl(logoUrl);
}